import type { VirtualItem } from '@tanstack/react-virtual'
import { useMemo } from 'react'
import { useAccountsFilteredBalances } from '@/src/queries/hooks/balances'
import { useSupportedChainsMap } from '@/src/queries/hooks/chains'
import { useTokenAcrossChains } from '@/src/queries/hooks/tokens'
import { useTokenSelection } from '@/src/stores/token-selection/actions'
import TokenItem from '../form/token-selector/token-item'
import type { OnSelectToken, PopularTokenEntry } from './types'

export function PopularTokenItem({
  onSelectToken,
  tokens,
  virtualRow,
  direction = 'from',
  isSearching = false,
}: {
  tokens: PopularTokenEntry[]
  onSelectToken: OnSelectToken
  virtualRow: VirtualItem
  direction: 'from' | 'to' | null
  isSearching?: boolean
}) {
  const { filteredNetwork } = useTokenSelection()

  const {
    variant_display_name,
    ticker,
    variant_logo_img_url,
    preferred_chain_id,
    denoms_by_chain,
    isBalance,
  } = tokens[virtualRow.index]

  const { chain_id, denom } = useMemo(
    () =>
      denoms_by_chain?.find(
        ({ chain_id }) => chain_id === preferred_chain_id,
      ) ?? denoms_by_chain[0],
    [denoms_by_chain, preferred_chain_id],
  )

  const { data: tokenAcrossChain } = useTokenAcrossChains(chain_id, denom)
  const { data: supportedChains } = useSupportedChainsMap()

  const denoms = useMemo(() => {
    if (
      filteredNetwork ||
      tokens.length === 1 ||
      (denoms_by_chain.length === 1 && isBalance)
    ) {
      return [
        {
          denom,
          chainName:
            filteredNetwork?.chain_name ??
            supportedChains?.get(chain_id)?.chain_name ??
            '',
          chainImgUrl: supportedChains?.get(chain_id)?.chain_img,
        },
      ]
    }

    if (
      !tokenAcrossChain ||
      !tokenAcrossChain.asset_across_chains ||
      !supportedChains
    )
      return []

    return tokenAcrossChain.asset_across_chains.map(({ denom, chain_id }) => ({
      denom,
      chainName: supportedChains.get(chain_id)?.chain_name ?? '',
      chainImgUrl: undefined,
    }))
  }, [
    chain_id,
    denom,
    denoms_by_chain.length,
    filteredNetwork,
    supportedChains,
    tokenAcrossChain,
    tokens.length,
    isBalance,
  ])

  const { balancesSummary } = useAccountsFilteredBalances({
    chainId: chain_id,
    denom,
    ticker,
    includeWalletKeyring: true, // Include pending account balances for trading
  })

  const { amount, usdValue } = useMemo(() => {
    if (!isSearching || balancesSummary.length === 0)
      return {
        amount: undefined,
        usdValue: undefined,
      }

    return {
      amount: balancesSummary[0].summary.normalized_amount,
      usdValue: balancesSummary[0].summary.value_usd,
    }
  }, [balancesSummary, isSearching])

  return (
    <TokenItem
      direction={direction ?? 'from'}
      chainId={chain_id}
      denom={denom}
      ticker={ticker}
      onSelectToken={() =>
        onSelectToken({
          chainId: chain_id,
          denom,
          ticker,
          tokenImg: variant_logo_img_url,
          displayName: variant_display_name,
          shouldDirectlySelect: denoms.length === 1 || !!amount || !!usdValue,
        })
      }
      size={virtualRow.size}
      start={virtualRow.start}
      variant_display_name={variant_display_name || ''}
      variant_logo_img_url={variant_logo_img_url || ''}
      denoms={denoms}
      amount={amount}
      usdValue={usdValue}
      chainImgUrl={denoms[0]?.chainImgUrl ?? ''}
      chainName={denoms[0]?.chainName ?? ''}
    />
  )
}
